import {
  Button,
  Stack,
  TextField,
  Typography,
  SelectChangeEvent,
} from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  generatePath,
  useNavigate,
  useParams,
  useMatch,
  Navigate,
} from "react-router-dom";
import {
  useGetPartnerQuery,
  useLazyGetHistoryCategoriesQuery,
} from "src/shared/api/partner";
import {
  EConfirmModalType,
  Loader,
  ModalComponent,
  SelectField,
} from "src/shared/components";
import { ContractBody } from "src/shared/components/contracts/ContractBody";
import { modalText } from "src/shared/constants/form.constants";
import { routes } from "src/shared/constants/routes.constants";
import { showApiError } from "src/shared/helpers/api.helpers";
import { useConfirmModal } from "src/shared/hooks";
import { EAgencies } from "src/shared/types/common.types";
import {
  useAttachContractOriginalMutation,
  useAttachTaxCertificateMutation,
  useDownloadPartnerContractDocxMutation,
  useGetPartnerComplexContractQuery,
  useGetPartnerContractDocxQuery,
  usePutPartnerComplexContractMutation,
} from "../../../model/api/api";
import { getPartnerComplexContractDefaultFormValues } from "../../../model/constants/formValues";
import { getPartnerComplexContractFormValues } from "../../../model/helpers/helpers";
import { IPartnerComplexContractFormValues } from "../../../model/types";
import { ComplexContractDialogFooter } from "./ComplexContractDialogFooter";
import { ComplexContractForm } from "./ComplexContractForm";

const CLOSE_REASON_STATES = new Set<number>([4, 5]);
type ContractState = number | string | { value: number | string; name: string };

const normalizeState = (s: ContractState): number =>
  typeof s === "object" && s !== null && "value" in s
    ? Number(s.value)
    : Number(s);

const downloadBlobUrl = (url: string, filename: string) => {
  const a = document.createElement("a");
  a.href = url;
  a.setAttribute("download", filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

const getGoBackRoute = (
  isIframe: boolean | null,
  partner_id?: string,
): string =>
  generatePath(
    isIframe ? routes.iframePartnerContracts : routes.partnerContractsList,
    {
      partner_id,
    },
  );

export function EitPartnerComplexContract() {
  const { partner_id, contract_id } = useParams();
  const navigate = useNavigate();
  const isIframeRoute = useMatch("/iframe/*");
  const goBackRoute = useMemo(
    () => getGoBackRoute(!!isIframeRoute, partner_id),
    [isIframeRoute, partner_id],
  );

  const { data: partner } = useGetPartnerQuery(partner_id || skipToken);
  const { data: docxHtmlTemplate } = useGetPartnerContractDocxQuery({
    partner_id: partner_id!,
    contract_id: contract_id!,
  });

  const {
    data: contract,
    isLoading,
    error: contractError,
  } = useGetPartnerComplexContractQuery(
    partner_id && contract_id ? { partner_id, contract_id } : skipToken,
  );

  const [putContract, { isLoading: isLoadingPut }] =
    usePutPartnerComplexContractMutation();

  const [
    attachContractOriginal,
    { isLoading: isLoadingAttachContractOriginal },
  ] = useAttachContractOriginalMutation();

  const [attachTaxCertificate, { isLoading: isLoadingAttachTaxCertificate }] =
    useAttachTaxCertificateMutation();

  const [
    downloadPartnerContractDocx,
    { isLoading: isLoadingDownloadPartnerContractDocx },
  ] = useDownloadPartnerContractDocxMutation();

  const [
    getHistoryCategories,
    { data: historyCategoriesTree, isFetching: isHistoryCategoriesFetching },
  ] = useLazyGetHistoryCategoriesQuery();

  const methods = useForm<IPartnerComplexContractFormValues>({
    mode: "onChange",
    defaultValues: getPartnerComplexContractDefaultFormValues({
      agency: partner?.agency.value,
    }),
  });
  const {
    reset,
    handleSubmit,
    formState: { isDirty },
  } = methods;

  const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | null>(null);

  const [guideId, setGuideId] = useState<number | undefined>(undefined);
  const [guideIdError, setGuideIdError] = useState<string | null>(null);

  const [pendingValues, setPendingValues] =
    useState<IPartnerComplexContractFormValues | null>(null);

  const initialStateRef = useRef<number | null>(null);

  const { modal, showModal } = useConfirmModal({
    onResolve: () => navigate(goBackRoute),
    confirmModalType: EConfirmModalType.WARNING,
    confirmBtnProps: { isLoading: false, title: modalText.continue },
    children: <Typography>{modalText.unsavedChanges}</Typography>,
    title: modalText.confirmAction,
  });

  const categoriesArray = useMemo(() => {
    if (Array.isArray(historyCategoriesTree)) return historyCategoriesTree;
    const data = historyCategoriesTree?.data;

    return Array.isArray(data) ? data : [];
  }, [historyCategoriesTree]);

  const reasonBlock = useMemo(() => {
    const cooperation = categoriesArray.find(
      (i: { id: number; name: string }) => i.id === 1,
    );

    return (
      cooperation?.children?.find(
        (c: { id: number; name: string }) => c.id === 52,
      ) || null
    );
  }, [categoriesArray]);

  const guideOptions = useMemo(
    () =>
      (reasonBlock?.children ?? []).map((c: { id: number; name: string }) => ({
        value: c.id,
        name: c.name,
      })),
    [reasonBlock],
  );

  useEffect(() => {
    if (!contract) return;
    const formVals = getPartnerComplexContractFormValues(contract);
    reset(formVals);

    const raw = formVals.contract_state as ContractState;
    const initial = normalizeState(raw);
    initialStateRef.current = Number.isNaN(initial) ? null : initial;
  }, [contract, reset]);

  const handleSaveContract = async (
    data: IPartnerComplexContractFormValues,
  ) => {
    try {
      const response = await putContract({
        id: Number(contract_id),
        ...data,
        contract_date: data.contract_date.format("YYYY-MM-DD"),
      }).unwrap();

      const fileAttachments = [
        {
          handler: attachContractOriginal,
          file: data.contract_original_file_path,
          errorMessage: "Не удалось прикрепить скан договора",
        },
        {
          handler: attachTaxCertificate,
          file: data.tax_certificate_path,
          errorMessage: "Не удалось прикрепить справку о налогообложении",
        },
      ];

      await Promise.all(
        fileAttachments.map(async ({ handler, file, errorMessage }) => {
          if (!file || typeof file === "string") return;
          try {
            await handler({
              partner_id: Number(partner_id),
              contract_id: response.data.id,
              file,
            });
          } catch (error) {
            showApiError(error);
            enqueueSnackbar(errorMessage, { variant: "warning" });
          }
        }),
      );

      navigate(goBackRoute);
    } catch (e) {
      showApiError(e);
    }
  };
  const handleExport = async () => {
    // если оригинального файла нет — генерим docx
    if (!contract?.contract_original_file_path) {
      try {
        const contractNumber =
          contract?.contract_number?.toString().trim() ?? "-";
        const url = await downloadPartnerContractDocx({
          partner_id: partner_id!,
          contract_id: contract_id!,
        }).unwrap();
        downloadBlobUrl(url, `Договор №${contractNumber}.docx`);

        return;
      } catch {
        enqueueSnackbar("Произошла ошибка", {
          variant: "error",
          handleClose: closeSnackbar,
        });

        return;
      }
    }

    // если есть — скачиваем PDF
    try {
      const contractNumber =
        contract?.contract_number?.toString().trim() ?? "-";
      const response = await fetch(
        contract.contract_original_file_path as string,
      );
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      downloadBlobUrl(downloadUrl, `Договор №${contractNumber}.docx`);
    } catch {
      enqueueSnackbar("Произошла ошибка", {
        variant: "error",
        handleClose: closeSnackbar,
      });
    }
  };

  const handleDownloadDocxTemplate = useCallback(async () => {
    const contractNumber = contract?.contract_number?.toString().trim() ?? "-";

    try {
      const url = await downloadPartnerContractDocx({
        partner_id: partner_id!,
        contract_id: contract_id!,
      }).unwrap();

      downloadBlobUrl(url, `Договор №${contractNumber}.docx`);
    } catch (error) {
      enqueueSnackbar("Произошла ошибка", {
        variant: "error",
        handleClose: closeSnackbar,
      });
    }
  }, [
    contract?.contract_number,
    downloadPartnerContractDocx,
    partner_id,
    contract_id,
  ]);

  const handlePrint = async () => {
    if (!contract?.contract_original_file_path) {
      if (!docxHtmlTemplate) return;
      const win = window.open("", "_blank");
      if (!win) return;
      win.document.open();
      win.document.write(docxHtmlTemplate);
      win.document.close();
      setTimeout(() => win.print(), 200);

      return;
    }
    window.open(contract.contract_original_file_path as string, "_blank");
  };

  const handleConfirmCloseWithReason = async () => {
    let hasError = false;

    if (!guideId) {
      setGuideIdError("Выберите причину расторжения");
      hasError = true;
    }
    if (!reason.trim()) {
      setReasonError("Укажите комментарий");
      hasError = true;
    }
    if (hasError || !pendingValues) return;

    const withNote: IPartnerComplexContractFormValues = {
      ...pendingValues,
      history_log_note: {
        comment: reason.trim(),
        history_id: reasonBlock?.id ?? 52,
        guide_id: guideId,
      },
    };

    setIsReasonModalOpen(false);
    setReason("");
    setReasonError(null);
    setGuideId(undefined);
    setGuideIdError(null);

    await handleSaveContract(withNote);
  };

  if (!partner_id || !contract_id) {
    return <Navigate to="/404" replace />;
  }

  if (
    contractError &&
    "status" in contractError &&
    contractError.status === 404
  ) {
    return <Navigate to="/404" replace />;
  }

  return (
    <ModalComponent
      open
      title={
        <Typography>
          <Typography component="span" color="primary">
            {contract?.contract_number}
          </Typography>
          <Typography component="span" color="textSecondary">
            &nbsp;({partner?.name})
          </Typography>
        </Typography>
      }
      width={() => ({ xs: "100%", sm: "95vw", xxl: 1520 })}
      boxProps={{ sx: { height: "90vh" } }}
      disableBodyPadding
      initialBodyShadow
      onClose={isDirty ? showModal : () => navigate(goBackRoute)}
      modalFooter={
        <ComplexContractDialogFooter
          isEdit
          isLoading={
            isLoadingPut ||
            isLoadingAttachContractOriginal ||
            isLoadingAttachTaxCertificate
          }
          isFileExportLoading={isLoadingDownloadPartnerContractDocx}
          onExport={handleExport}
          onPrint={handlePrint}
          onCancel={isDirty ? showModal : () => navigate(goBackRoute)}
          onSubmit={handleSubmit((values) => {
            const next = normalizeState(values.contract_state as ContractState);
            const prev = initialStateRef.current;

            const isTransitionFromActiveToClosing =
              prev === 2 && CLOSE_REASON_STATES.has(next);

            if (isTransitionFromActiveToClosing) {
              setPendingValues(values);
              setIsReasonModalOpen(true);
              getHistoryCategories(undefined);

              return;
            }
            handleSaveContract(values);
          })}
        />
      }
    >
      {isLoading && <Loader position="absolute" blur />}
      {modal}

      {isReasonModalOpen && (
        <ModalComponent
          open
          small
          title="Добавить заметку / Примечание"
          isLoading={isHistoryCategoriesFetching}
          onClose={() => {
            setIsReasonModalOpen(false);
            setReason("");
            setReasonError(null);
            setPendingValues(null);
            setGuideId(undefined);
            setGuideIdError(null);
          }}
          modalFooter={
            <Stack direction="row" spacing={2} justifyContent="flex-end" p={2}>
              <Button
                variant="text"
                onClick={() => {
                  setIsReasonModalOpen(false);
                  setReason("");
                  setReasonError(null);
                  setPendingValues(null);
                  setGuideId(undefined);
                  setGuideIdError(null);
                }}
              >
                Отмена
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmCloseWithReason}
                disabled={
                  isHistoryCategoriesFetching ||
                  isLoadingPut ||
                  isLoadingAttachContractOriginal ||
                  isLoadingAttachTaxCertificate
                }
              >
                Сохранить
              </Button>
            </Stack>
          }
        >
          <Stack p={2} spacing={2}>
            <Typography variant="body2" color="text.secondary">
              История сотрудничества
            </Typography>

            <TextField
              fullWidth
              label="Причины расторжения"
              value={reasonBlock?.name ?? 52}
              disabled
            />

            <SelectField
              fullWidth
              label="Выберите причину расторжения"
              value={guideId ?? ""}
              options={guideOptions}
              error={!!guideIdError}
              helperText={guideIdError || " "}
              onChange={(e: SelectChangeEvent<number>) => {
                const raw = e.target.value;
                const num =
                  typeof raw === "string" ? Number(raw) : (raw as number);
                setGuideId(Number.isNaN(num) ? undefined : num);
                if (guideIdError) setGuideIdError(null);
              }}
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Введите комментарий"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (reasonError) setReasonError(null);
              }}
              error={!!reasonError}
              helperText={reasonError || " "}
              slotProps={{ htmlInput: { maxLength: 1200 } }}
            />
          </Stack>
        </ModalComponent>
      )}

      <FormProvider {...methods}>
        <ContractBody
          pdfUrl={contract?.contract_original_file_path}
          html={docxHtmlTemplate}
          form={
            <ComplexContractForm
              isEdit
              agency={partner?.agency.value || EAgencies.IContext}
              handleDownloadDocxTemplate={handleDownloadDocxTemplate}
            />
          }
        />
      </FormProvider>
    </ModalComponent>
  );
}
